import { Injectable } from '@angular/core';
import { FlightSearchPayload, ServiceExtras } from '../models/flight-payload';
import { getProductCode } from '../models/product-mapping';
import { serviceMappers } from './payload-mapper';
import { CargoFlightHeaderRequest } from '../models/cargo-flight-header-model';


export type ServicePayload<S extends keyof ServiceExtras> = FlightSearchPayload & ServiceExtras[S];

export function buildPayload<S extends keyof ServiceExtras>(
  service: S,
  base: FlightSearchPayload
): ServicePayload<S> {
  const productCode = getProductCode(base.product ?? '');
  const enrichedPayload = { ...base, productCode };

  const mapped = serviceMappers[service](enrichedPayload);

  return { ...mapped, productCode } as ServicePayload<S>;
}

export function buildHeaderPayload(base: FlightSearchPayload) {
  return {
    ViewFlightHeaderRequest: {
      ProductCode: getProductCode(base.product ?? ''),
      DatedFlightLeg: {
        OperatorCarrierCode: base.flightNumbers?.[0]?.slice(0, 2) ?? '',
        OperationalFlightNumber: base.flightNumbers?.[1] ?? '',
        OriginStation: base.departureStation ?? '',
        ScheduledDepartureDateLocal: formatDate(base.flightDate) ?? '',
      },
    },
  };
}


export function toIsoLocalDateTime(input: string | Date): string {
  const d = typeof input === 'string' ? new Date(input) : input;
  if (Number.isNaN(d.getTime())) return ''; 

  const pad = (n: number) => n.toString().padStart(2, '0');

  const year = d.getFullYear();
  const month = pad(d.getMonth() + 1);
  const day = pad(d.getDate());
  const hours = pad(d.getHours());
  const minutes = pad(d.getMinutes());
  const seconds = pad(d.getSeconds());

  
  return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}`;
}


export function buildCargoFlightHeaderRequest(base: FlightSearchPayload): CargoFlightHeaderRequest {

 
  const carrier = base.flightNumbers?.[0] ?? '';
  const rawFlightNo = base.flightNumbers?.[1] ?? '';
 

  const match = /^(\d+)([A-Za-z]?)$/.exec(rawFlightNo);
  const flightNo = match ? match[1] : rawFlightNo;
  const flightNoSfx = match?.[2] || '';

  return {
    carrier,
    flightNo,

    origin: base.departureStation ?? '',
      
    destination: (base as any).destinationStation ?? (base as any).arrivalStation ?? '',
    productCode: base.productCode
      ? base.productCode
      : getProductCode(base.product ?? ''),

    depDateTime: toIsoLocalDateTime(base.flightDate),

    flightNoSfx: flightNoSfx || undefined,
    iataServiceType: base.iataServiceType || undefined
  };
}



function formatDate(date: string): string {

  const [day, month, year] = date.split('-');
  const months: Record<string, string> = {
    JAN: '01',
    FEB: '02',
    MAR: '03',
    APR: '04',
    MAY: '05',
    JUN: '06',
    JUL: '07',
    AUG: '08',
    SEP: '09',
    OCT: '10',
    NOV: '11',
    DEC: '12',
  };
  return `${year}-${months[month]}-${day}T00:00:00`;
}



export function uniqueFlightKey(p: FlightSearchPayload): string {
  return `${p.flightNumbers?.[0] || ''}-${p.flightNumbers?.[1] || ''}-${p.flightDate}-${p.departureStation}`;
}

@Injectable({ providedIn: 'root' })
export class PayloadFactory {
  build<S extends keyof ServiceExtras>(service: S, base: FlightSearchPayload): ServicePayload<S> {
    const productCode = getProductCode(base.product ?? '');
    const mapped = serviceMappers;

    return { ...mapped, productCode } as unknown as ServicePayload<S>;
  }
}
